import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { DocumentTextIcon } from "@heroicons/react/24/outline";
import Header from "common/components/Header/Header";
import Button from "common/components/Button/Button";
import Collapsable from "common/components/Collapsable/Collapsable";
import useAuth from "common/hooks/useAuth";
import useItem from "common/hooks/useItem";
import useRouter from "common/hooks/use-router";
import AlertsContext from "common/providers/alerts";
import generatePDF from "common/utils/generatePdf";
import { SavedBalanceSheet as SavedBalanceSheetType } from "types/SavedBalanceSheet";
import useBalanceSheetData from "pages/BalanceSheets/useBalanceSheetData";
import BalanceSheetReport from "pages/BalanceSheets/components/BalanceSheetReport";
import BalanceSheetDisplayToggles, {
  DEFAULT_DISPLAY_OPTIONS,
} from "pages/BalanceSheets/components/BalanceSheetDisplayToggles";
import BalanceSheetComments from "./components/BalanceSheetComments";

function SavedBalanceSheet() {
  const { t } = useTranslation();
  const router = useRouter();
  const { setAlert } = useContext(AlertsContext);
  const { user: currentUser } = useAuth();

  const id = router.query.id as string;

  const {
    item: savedBalanceSheet,
    error,
    isFetching: isFetchingSavedBalanceSheet,
  } = useItem<SavedBalanceSheetType>("saved-balance-sheets/data/list", id);

  const {
    balance,
    setBalance,
    isFetching,
    error: balanceError,
    fetchBalance,
  } = useBalanceSheetData();

  const [displayOptions, setDisplayOptions] = useState(DEFAULT_DISPLAY_OPTIONS);
  const [isExporting, setIsExporting] = useState(false);
  const [isSendingMail, setIsSendingMail] = useState(false);
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (error) {
      setAlert(error, "danger");
    }
  }, [error, setAlert]);

  // A frozen balance sheet is read from its snapshot, a live one is recomputed
  // from the filters it was saved with.
  useEffect(() => {
    if (!savedBalanceSheet) {
      return;
    }

    setDisplayOptions({
      ...DEFAULT_DISPLAY_OPTIONS,
      ...(savedBalanceSheet.displayOptions ?? {}),
    });

    if (savedBalanceSheet.isFrozen && savedBalanceSheet.snapshot) {
      setBalance(savedBalanceSheet.snapshot);
    } else {
      fetchBalance(savedBalanceSheet.filters);
    }
  }, [savedBalanceSheet, setBalance, fetchBalance]);

  const canDownloadBalanceSheets =
    (currentUser?.isAdmin ||
      currentUser?.isSuperadmin ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        currentUser?.administrationPermissions?.["balance-sheet"]?.download)) ??
    false;

  const exportToPDF = async () => {
    setIsExporting(true);
    const result = await generatePDF({ chartRef });
    if (result && result.blob) {
      const { blob, filename } = result;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
    setIsExporting(false);
  };

  const sendEmailWithPDF = async () => {
    setIsSendingMail(true);
    const result = await generatePDF({ chartRef });
    if (result && result.blob) {
      const { blob, filename } = result;

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      const subject = encodeURIComponent(t("balance.mailObject"));
      const body = encodeURIComponent(
        t("balance.mailContent", {
          startDate: dayjs(balance?.startDate).format("LL"),
          endDate: dayjs(balance?.endDate).format("LL"),
        })
      );

      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    }
    setIsSendingMail(false);
  };

  if (isFetchingSavedBalanceSheet || !savedBalanceSheet) {
    return null;
  }

  return (
    <div className="mx-auto max-w-full py-4 px-4 text-primary flex flex-col gap-6">
      <Header
        title={t("titlePages.saved-balance-sheets")}
        Icon={DocumentTextIcon}
        noAdd
        canGoBack
        onGoBack={() => router.navigate("/saved-balance-sheets")}
        isBeforeFilters
      />

      <div className="flex flex-col xl:flex-row justify-between gap-4 mt-2">
        <div className="flex flex-col gap-1">
          <h2 className="text-2xl font-bold">{savedBalanceSheet.name}</h2>
          <div className="flex flex-row flex-wrap items-center gap-3 text-sm text-slate-500">
            <span>
              {dayjs(savedBalanceSheet.startDate).format("L")}
              {" → "}
              {dayjs(savedBalanceSheet.endDate).format("L")}
            </span>
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${
                savedBalanceSheet.isFrozen
                  ? "bg-emerald-100 text-emerald-800"
                  : "bg-orange-100 text-orange-800"
              }`}
            >
              {savedBalanceSheet.isFrozen
                ? t("balance.frozenOn", {
                    date: dayjs(savedBalanceSheet.createdAt).format("LLL"),
                  })
                : t("balance.liveData")}
            </span>
            {savedBalanceSheet.authorUsername && (
              <span>
                {t("balance.byAuthorDate", {
                  author: savedBalanceSheet.authorUsername,
                  date: dayjs(savedBalanceSheet.createdAt).format("LLL"),
                })}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-row gap-4 self-start">
          <Button
            title={t("actions.sendByMail")}
            type="primary-line"
            icon="IoPaperPlaneOutline"
            onClick={sendEmailWithPDF}
            loading={isSendingMail}
            disabled={isSendingMail || isFetching || !canDownloadBalanceSheets}
          />
          <Button
            title={t("actions.exportPdf")}
            type="neutral-line"
            icon="AiOutlineDownload"
            onClick={exportToPDF}
            loading={isExporting}
            disabled={isExporting || isFetching || !canDownloadBalanceSheets}
          />
        </div>
      </div>

      <div className="rounded-2xl bg-slate-200/70 p-4">
        <Collapsable
          title={t("forms.graphVisibility")}
          content={
            <div className="pt-4">
              <BalanceSheetDisplayToggles
                value={displayOptions}
                onChange={setDisplayOptions}
              />
            </div>
          }
        />
      </div>

      {balanceError ? (
        <div className="white-box p-6 flex flex-col items-center gap-3 text-center">
          <p className="text-lg">{t("balance.cannotReplay")}</p>
          <p className="text-sm text-slate-500">{balanceError}</p>
        </div>
      ) : null}

      <div className="flex flex-col xl:flex-row gap-6 items-start">
        <div className="flex-1 min-w-0 w-full">
          <BalanceSheetReport
            balance={balance}
            displayOptions={displayOptions}
            chartRef={chartRef}
          />
        </div>
        <div className="w-full xl:w-[360px] xl:shrink-0 xl:sticky xl:top-4">
          <BalanceSheetComments savedBalanceSheetId={Number(id)} />
        </div>
      </div>
    </div>
  );
}

export default SavedBalanceSheet;
